import { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { toast } from 'react-toastify';
import {
  Dialog,
  Skeleton,
  Flex,
  DataList,
  Button,
  Text,
  Checkbox,
  Separator,
} from '@scale/scaleui-radix';
import {
  endSession,
  useGetLiveSession,
  lookupUserIdsOrEmails,
} from 'react-scale/components/WebinarSessions/api';
import { findNextSession } from 'react-scale/components/WebinarSessions/helpers';
import moment from 'moment-timezone';

interface EndPreviousSessionModalProps {
  toggleModal: () => void;
  sessions: any[];
}

const showToast = (message: string, type: 'error' | 'success') => {
  toast(message, {
    type,
    autoClose: 5000,
    position: 'top-right',
  });
};

export const EndPreviousSessionModal: React.FC<EndPreviousSessionModalProps> = ({
  toggleModal,
  sessions,
}) => {
  const [confirmIssueChecked, setConfirmIssueChecked] = useState(false);
  const [confirmReachOutChecked, setConfirmReachOutChecked] = useState(false);

  const nextSession = findNextSession(sessions);

  const { data: previousSessionData, isLoading: isPreviousSessionDataLoading } = useGetLiveSession({
    sessionId: nextSession._id,
    enabled: !!nextSession._id,
  });

  const { data: hostsData, isLoading: isHostsDataLoading } = useQuery(
    ['webinar-sessions-hosts', previousSessionData?.hostedBy],
    () => lookupUserIdsOrEmails(previousSessionData?.hostedBy || []),
    {
      enabled: !!previousSessionData?.hostedBy && previousSessionData?.hostedBy.length > 0,
    },
  );

  const { mutate: endPreviousSession, isLoading: isEndingPreviousSession } = useMutation(
    async () => {
      if (previousSessionData) {
        await endSession(previousSessionData._id);
      }
    },
    {
      onSuccess: () => {
        showToast('Session ended successfully', 'success');
        toggleModal();
      },
      onError: (error: Error) => {
        showToast(error.message, 'error');
      },
    },
  );

  return (
    <Dialog.Root open={true} onOpenChange={toggleModal}>
      <Dialog.Content size="1" maxWidth="800px">
        <Flex direction="column" gap="4" p="4">
          <Text weight="medium" size="4">
            End Previous Session
          </Text>
          <Text color="neutral">
            If you cannot start your webinar and see "Host has another meeting in progress," please
            contact the host of the previous session to confirm it has ended before clicking "End
            Previous Session."
          </Text>
          <Separator orientation="horizontal" size="4" />
          <Skeleton loading={isPreviousSessionDataLoading || isHostsDataLoading}>
            {previousSessionData ? (
              <Flex direction="column" gap="4">
                <DataList.Root orientation={{ initial: 'vertical', sm: 'horizontal' }}>
                  <DataList.Item>
                    <DataList.Label minWidth="160px">Session name</DataList.Label>
                    <DataList.Value>{previousSessionData.name}</DataList.Value>
                  </DataList.Item>
                  <DataList.Item>
                    <DataList.Label minWidth="160px">Session ID</DataList.Label>
                    <DataList.Value>
                      {previousSessionData._id ? previousSessionData._id : 'N/A'}
                    </DataList.Value>
                  </DataList.Item>
                  <DataList.Item>
                    <DataList.Label minWidth="160px">Project ID</DataList.Label>
                    <DataList.Value>
                      {previousSessionData.project ? previousSessionData.project : 'N/A'}
                    </DataList.Value>
                  </DataList.Item>
                  <DataList.Item>
                    <DataList.Label minWidth="160px">Host(s)</DataList.Label>
                    <DataList.Value>
                      <Flex direction="column" gap="2">
                        {hostsData?.map(host => (
                          <div key={host.id}>{host.email}</div>
                        ))}
                      </Flex>
                    </DataList.Value>
                  </DataList.Item>
                  <DataList.Item>
                    <DataList.Label minWidth="160px">Start time</DataList.Label>
                    <DataList.Value>
                      {moment
                        .tz(previousSessionData.eventStart, 'UTC')
                        .tz('America/Los_Angeles')
                        .format('MMMM D, YYYY, h:mm:ss A (z)')}
                    </DataList.Value>
                  </DataList.Item>
                  <DataList.Item>
                    <DataList.Label minWidth="160px">End time</DataList.Label>
                    <DataList.Value>
                      {moment
                        .tz(previousSessionData.eventStart, 'UTC')
                        .add(previousSessionData.duration, 'minutes')
                        .tz('America/Los_Angeles')
                        .format('MMMM D, YYYY, h:mm:ss A (z)')}
                    </DataList.Value>
                  </DataList.Item>
                  <DataList.Item>
                    <DataList.Label minWidth="160px">Zoom service account</DataList.Label>
                    <DataList.Value>{previousSessionData.zoomServiceAccount}</DataList.Value>
                  </DataList.Item>
                </DataList.Root>
                <Separator orientation="horizontal" size="4" />
                <Flex align="center" gap="2">
                  <Checkbox
                    size="1"
                    checked={confirmIssueChecked}
                    onCheckedChange={checked => setConfirmIssueChecked(checked === true)}
                  />{' '}
                  I cannot start my webinar because "Host has another meeting in progress."
                </Flex>
                <Flex align="center" gap="2">
                  <Checkbox
                    size="1"
                    checked={confirmReachOutChecked}
                    onCheckedChange={checked => setConfirmReachOutChecked(checked === true)}
                  />{' '}
                  I have reached out to the host of the previous session to confirm it has ended.
                </Flex>
                <Flex ml="auto">
                  <Button
                    onClick={() => endPreviousSession()}
                    disabled={
                      isEndingPreviousSession || !confirmIssueChecked || !confirmReachOutChecked
                    }
                  >
                    End Previous Session
                  </Button>
                </Flex>
              </Flex>
            ) : (
              <Text>No previous in-progress session found for {nextSession.zoomMeeting.host}.</Text>
            )}
          </Skeleton>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
};
